import { useState, type ReactNode } from "react";
import { useStore } from "../state/store";
import { CopyBlock } from "./CopyBlock";

/**
 * Blocking state: the Gateway refuses this browser origin.
 *
 * Like the pairing screen, this is a moment where the app cannot proceed on its
 * own and the fix lives somewhere else — so it gets the whole screen and the
 * exact values, already filled in.
 *
 * The check itself is not in the way of anything; it is the reason a random web
 * page cannot open a socket to somebody's Gateway. Saying so matters: a user
 * who understands the check is being asked to *widen* it deliberately, rather
 * than to disable a nuisance.
 *
 * Four ways are offered because the person reading this is, by definition, on a
 * device that cannot reach the Gateway. On a phone there is no terminal, so
 * asking the agent to do it — through a chat that already works — comes first.
 */

type Method = "agent" | "controlUi" | "cli" | "docker";

const METHODS: Array<[Method, string]> = [
  ["agent", "Ask OpenClaw"],
  ["controlUi", "Control UI"],
  ["cli", "Terminal"],
  ["docker", "Docker"],
];

export function OriginScreen() {
  const settings = useStore((store) => store.settings);
  const state = useStore((store) => store.connectionState);
  const connection = useStore((store) => store.connection);
  const disconnect = useStore((store) => store.disconnect);
  const [method, setMethod] = useState<Method>("agent");

  const origin = window.location.origin;

  /**
   * Asking the agent is the safest of the four: it can read the current list and
   * add to it, where `config set` overwrites the array wholesale.
   */
  const agentPrompt = `Please add ${origin} to gateway.controlUi.allowedOrigins — keep the existing entries — and restart the Gateway afterwards.`;

  const cliRead = "openclaw config get gateway.controlUi.allowedOrigins";
  // `--strict-json` is what makes this an array instead of a string, and the
  // list here has to be the *complete* new list: `config set` replaces it, and
  // gateway.controlUi.allowedOrigins is not one of the protected paths that
  // refuse a shrinking write.
  const cliCommand = `openclaw config set gateway.controlUi.allowedOrigins '["${origin}", "http://127.0.0.1:18789", "http://localhost:18789"]' --strict-json`;
  const dockerEnv = `OPENCLAW_GATEWAY_CONTROLUI_ALLOWEDORIGINS='["${origin}", "http://127.0.0.1:18789", "http://localhost:18789"]'`;

  function Step({ index, title, children }: { index: number; title: string; children: ReactNode }) {
    return (
      <li>
        <span className="step-index">{index}</span>
        <div>
          <p className="step-title">{title}</p>
          {children}
        </div>
      </li>
    );
  }

  return (
    <div className="setup">
      <div className="setup-card">
        <div className="setup-logo" aria-hidden="true">
          🚧
        </div>
        <h1>This address is not allowed</h1>
        <p className="setup-lead">
          The Gateway at <strong>{settings?.url}</strong> only accepts browser connections from
          addresses listed in its configuration. <strong>{origin}</strong> is not there yet. Add
          it once and that is the end of it.
        </p>

        <div className="method-tabs" role="tablist" aria-label="Ways to allow it">
          {METHODS.map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={method === value}
              className="method-tab"
              onClick={() => setMethod(value)}
            >
              {label}
            </button>
          ))}
        </div>

        {method === "agent" ? (
          <>
            <p className="method-lead">
              Quickest from a phone: your agent is allowed to change its own configuration.
              Write to it wherever you already reach it — terminal, Telegram, WhatsApp, the
              Control UI.
            </p>
            <ol className="steps">
              <Step index={1} title="Send this message to OpenClaw">
                <CopyBlock value={agentPrompt} />
                <p className="field-hint">
                  It reads the list, appends the address and leaves the existing entries alone —
                  which is what makes this the safest of the four. Depending on its settings it
                  asks first; confirm that.
                </p>
              </Step>
              <Step index={2} title="Wait for the restart">
                <p className="field-hint">
                  The Gateway is away for a moment. Then hit “Reconnect” below.
                </p>
              </Step>
            </ol>
          </>
        ) : null}

        {method === "controlUi" ? (
          <>
            <p className="method-lead">
              No terminal, but at the Gateway machine: its own interface can edit the
              configuration.
            </p>
            <ol className="steps">
              <Step index={1} title="Open the Control UI on the Gateway machine">
                <CopyBlock value="http://127.0.0.1:18789" />
              </Step>
              <Step index={2} title="Gear at the bottom left → Infrastructure → gateway">
                <p className="field-hint">
                  There, under <code>controlUi</code>, the <code>allowedOrigins</code> field.
                </p>
              </Step>
              <Step index={3} title="Add this address as a new entry">
                <CopyBlock value={origin} />
                <p className="field-hint">
                  As an additional entry — leave the existing ones in place. Then save and
                  restart the Gateway.
                </p>
              </Step>
            </ol>
          </>
        ) : null}

        {method === "cli" ? (
          <>
            <p className="method-lead">On the Gateway machine, three commands — the first one matters.</p>
            <p className="warn-block">
              <strong>Look before you write.</strong> <code>config set</code> replaces the list
              wholesale, and this path is not one of the protected ones: a call that leaves
              entries out is not refused, it is carried out. Anyone who had allowed a LAN address
              or a tunnel locks themselves out that way.
            </p>
            <ol className="steps">
              <Step index={1} title="Look at the existing list">
                <CopyBlock value={cliRead} />
              </Step>
              <Step index={2} title="Set the list again — with everything from step 1">
                <CopyBlock value={cliCommand} />
                <p className="field-hint">
                  The suggestion carries this origin and the two local addresses. Anything else
                  step 1 printed belongs in there too. The <code>--strict-json</code> has to stay,
                  or the text lands in the configuration as a string instead of a list.
                </p>
              </Step>
              <Step index={3} title="Restart the Gateway">
                <CopyBlock value="openclaw gateway restart" />
              </Step>
            </ol>
          </>
        ) : null}

        {method === "docker" ? (
          <>
            <p className="method-lead">
              In a container the list belongs in the environment, or the next rebuild loses it
              again.
            </p>
            <ol className="steps">
              <Step index={1} title="Set the variable (docker-compose.yml or .env)">
                <CopyBlock value={dockerEnv} />
                <p className="field-hint">
                  Here too it is the complete list. If the variable is already in your compose
                  file, extend it there instead of overwriting it.
                </p>
              </Step>
              <Step index={2} title="Restart the container">
                <CopyBlock value="docker compose up -d --force-recreate" />
              </Step>
            </ol>
          </>
        ) : null}

        <dl className="detail-list">
          <div>
            <dt>This origin</dt>
            <dd>
              <CopyBlock value={origin} inline truncate />
            </dd>
          </div>
          {state.diagnostic ? (
            <div>
              <dt>What the Gateway said</dt>
              <dd className="detail-plain">{state.diagnostic}</dd>
            </div>
          ) : null}
        </dl>

        <details className="origin-why">
          <summary>Why is anyone asking me this?</summary>
          <p>
            Without this check, any web page you happen to have open could open a connection to
            your Gateway in the background. So your Gateway decides which addresses it knows —
            not the address itself. Which is exactly why cllw cannot settle this on its own.
          </p>
          <p>
            You can usually skip the entry altogether by running cllw on the Gateway machine
            itself (<code>npm run preview</code>, then <code>http://localhost:5173</code>):
            loopback addresses are generally trusted there. If your Gateway turns those down
            too, the same route works — with the local address instead.
          </p>
        </details>

        <div className="sheet-actions" style={{ justifyContent: "space-between" }}>
          <button
            type="button"
            className="button-ghost"
            onClick={() => disconnect({ forgetDevice: false })}
          >
            Different connection
          </button>
          <button type="button" className="button-primary" onClick={() => connection?.retryNow()}>
            Reconnect
          </button>
        </div>
      </div>
    </div>
  );
}
